import { useEffect, useState, useRef } from "react";
import {
  assignOutputResource,
  globalOutputSettings,
  uid,
  isProofOutput,
  type Project,
  type OutputProfile,
  type Resource,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { Button, Control } from "./components";
export function OutputPresets({
  project,
  output,
  onChange,
  notify,
}: {
  project: Project;
  output: OutputProfile;
  onChange: (p: Project) => void;
  notify: (message: string) => void;
}) {
  const services = useAuthoringServices();
  const [catalog, setCatalog] = useState<Resource[]>([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const scope = `${project.tenantId}:${project.id}:${output.id}`;
  const latest = useRef<
    | { scope: string; project: Project; save: typeof services.saveResource }
    | undefined
  >(undefined);
  latest.current = { scope, project, save: services.saveResource };
  useEffect(
    () => () => {
      latest.current = undefined;
    },
    [],
  );
  useEffect(() => {
    setName("");
    setSaving(false);
  }, [scope, services.saveResource]);
  useEffect(() => {
    let active = true;
    setCatalog([]);
    if (services.listResources)
      void services
        .listResources()
        .then((rows) => {
          if (active) setCatalog(rows);
        })
        .catch((error) => {
          if (active) notify(error.message);
        });
    return () => {
      active = false;
    };
  }, [project.tenantId, services.listResources]);
  const allResources: Resource[] = [...catalog, ...project.resourceSnapshots];
  const rows = allResources.filter(
    (r, i, all) =>
      r.tenantId === project.tenantId &&
      (r.kind === "output"
        ? !r.projectId
        : !r.projectId || r.projectId === project.id) &&
      !r.archived &&
      !all
        .slice(0, i)
        .some((other) => other.id === r.id && other.version === r.version),
  );
  const choose = (key: string, kind: "production" | "sheet" | "output") => {
    try {
      if (!key) {
        onChange({
          ...project,
          outputs: project.outputs.map((o) =>
            o.id !== output.id
              ? o
              : kind === "output"
                ? { ...o, outputPreset: undefined }
                : kind === "production"
                  ? {
                      ...o,
                      production: undefined,
                      productionProfile: undefined,
                    }
                  : {
                      ...o,
                      imposition: { ...o.imposition, enabled: false },
                      sheetProfile: undefined,
                    },
          ),
        });
        return;
      }
      const row = rows.find(
        (r) => `${r.id}:${r.version}` === key && r.kind === kind,
      );
      if (!row) throw new Error("Preset is unavailable");
      onChange(assignOutputResource(project, output.id, row));
    } catch (error) {
      notify((error as Error).message);
    }
  };
  const save = async (kind: "production" | "sheet" | "output") => {
    if (!services.saveResource || saving) return;
    setSaving(true);
    try {
      const resource = await services.saveResource({
        id: uid("resource"),
        tenantId: project.tenantId,
        ...(kind !== "output" ? { projectId: project.id } : {}),
        kind,
        name:
          name.trim() ||
          `${project.name} ${kind === "production" ? "bleed" : kind === "output" ? output.name : "sheet"}`,
        version: 0,
        value:
          kind === "output"
            ? globalOutputSettings(output)
            : kind === "production"
              ? output.production || project.pages[0].production
              : output.imposition,
      });
      if (
        latest.current?.scope !== scope ||
        latest.current.save !== services.saveResource
      )
        return;
      setCatalog((rows) => [...rows, resource]);
      onChange(
        assignOutputResource(latest.current.project, output.id, resource),
      );
      setName("");
      notify("Reusable preset saved and assigned.");
    } catch (error) {
      if (
        latest.current?.scope === scope &&
        latest.current.save === services.saveResource
      )
        notify((error as Error).message);
    } finally {
      if (
        latest.current?.scope === scope &&
        latest.current.save === services.saveResource
      )
        setSaving(false);
    }
  };
  return (
    <section aria-label="Reusable production presets">
      <Control
        label="Output settings preset"
        note="Global profiles are assigned as versioned settings to this project's output stage."
      >
        <select
          value={
            output.outputPreset
              ? `${output.outputPreset.id}:${output.outputPreset.version}`
              : ""
          }
          onChange={(e) => choose(e.target.value, "output")}
        >
          <option value="">Custom / choose saved output settings</option>
          {rows
            .filter(
              (r) =>
                r.kind === "output" &&
                (!(r.value as OutputProfile).role ||
                  !output.role ||
                  (r.value as OutputProfile).role === output.role),
            )
            .map((r) => (
              <option
                key={`${r.id}:${r.version}`}
                value={`${r.id}:${r.version}`}
              >
                {r.name} · v{r.version}
              </option>
            ))}
        </select>
      </Control>
      <Control label="Page bleed profile">
        <select
          value={
            output.productionProfile
              ? `${output.productionProfile.id}:${output.productionProfile.version}`
              : ""
          }
          onChange={(e) => choose(e.target.value, "production")}
        >
          <option value="">
            {output.production
              ? "Custom profile settings"
              : "Inherit document page settings"}
          </option>
          {rows
            .filter((r) => r.kind === "production")
            .map((r) => (
              <option
                key={`${r.id}:${r.version}`}
                value={`${r.id}:${r.version}`}
              >
                {r.name} · v{r.version}
              </option>
            ))}
        </select>
      </Control>
      {!isProofOutput(output) && (
        <Control label="Sheet / paper preset">
          <select
            value={
              output.sheetProfile
                ? `${output.sheetProfile.id}:${output.sheetProfile.version}`
                : ""
            }
            onChange={(e) => choose(e.target.value, "sheet")}
          >
            <option value="">
              {output.imposition.enabled
                ? "Custom sheet settings"
                : "Individual product pages"}
            </option>
            {rows
              .filter((r) => r.kind === "sheet")
              .map((r) => (
                <option
                  key={`${r.id}:${r.version}`}
                  value={`${r.id}:${r.version}`}
                >
                  {r.name} · v{r.version}
                </option>
              ))}
          </select>
        </Control>
      )}
      {services.saveResource && (
        <details>
          <summary>Save reusable settings</summary>
          <Control label="Preset name">
            <input
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Optional descriptive name"
            />
          </Control>
          <Button disabled={saving} onClick={() => void save("production")}>
            Save bleed preset
          </Button>
          <Button disabled={saving} onClick={() => void save("output")}>
            Save output preset
          </Button>
          {!isProofOutput(output) && (
            <Button
              disabled={saving || !output.imposition.enabled}
              onClick={() => void save("sheet")}
            >
              Save sheet preset
            </Button>
          )}
        </details>
      )}
    </section>
  );
}
